
import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useRecipes } from "@/contexts/RecipesContext";

const NotFound = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { recipes } = useRecipes();
  const isAdmin = user?.role === 'admin';
  
  // Check if this is an admin route that might be mistyped
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Check if this is a recipe route that doesn't exist (using the correct singular form /receta/)
  const isRecipeRoute = location.pathname.startsWith('/receta/') || location.pathname.startsWith('/recetas/');
  
  // Extract the recipe ID - checking both numeric IDs and alphanumeric IDs with dashes
  // This handles both /receta/12345 and /receta/12345-slug formats
  // Also handles the special Lovable IDs format like 1744828482703-wn2ypyz
  // Also handles incorrect "/recetas/" (plural) URLs
  const recipeIdMatch = isRecipeRoute ? 
    location.pathname.match(/\/recetas?\/([^/]+)/) : null;
  const fullRecipeId = recipeIdMatch ? recipeIdMatch[1] : null;
  
  // For recipe search, try both the full ID and just the first part
  const numericId = fullRecipeId ? fullRecipeId.split('-')[0] : null;

  // Check if we can find a matching recipe with a different format
  const suggestedRecipe = isRecipeRoute && recipes && fullRecipeId ? 
    recipes.find(r => {
      if (!r.id) return false;
      const rId = r.id.toString();
      return rId === fullRecipeId || rId === numericId || 
             rId.startsWith(numericId + '-') || 
             fullRecipeId.startsWith(rId + '-');
    }) : null;

  // Log the 404 error for debugging
  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
      isRecipeRoute ? `Recipe ID: ${numericId}, Full ID: ${fullRecipeId}` : "",
      suggestedRecipe ? `Found possible matching recipe: ${suggestedRecipe.id}` : "",
      location.pathname.startsWith('/recetas/') ? "Incorrect plural form /recetas/ used instead of /receta/" : ""
    );
  }, [location.pathname, isRecipeRoute, numericId, fullRecipeId, suggestedRecipe]);

  return (
    <Layout>
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-6xl font-bold mb-4 text-vegana-primary">404</h1>
          <p className="text-2xl font-semibold text-gray-700 mb-4">Página no encontrada</p>
          <p className="text-gray-500 mb-8">
            Lo sentimos, no pudimos encontrar la página que estás buscando.
          </p>
          
          {isRecipeRoute && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
              <p className="font-medium mb-2">¿Buscando una receta?</p>
              {location.pathname.startsWith('/recetas/') && (
                <p className="mb-2">
                  ¡Ups! Parece que estás usando <strong>/recetas/</strong> en lugar de <strong>/receta/</strong>.
                  Todas nuestras recetas utilizan el formato singular.
                </p>
              )}
              {suggestedRecipe ? (
                <p>
                  {!location.pathname.startsWith('/recetas/') && "Parece que has encontrado un enlace con formato incorrecto."}
                  <Link to={`/receta/${suggestedRecipe.id}`} className="text-vegana-primary font-medium ml-1">
                    Haz clic aquí para ver la receta correcta.
                  </Link>
                </p>
              ) : (
                <p>El enlace a la receta podría estar desactualizado o la receta ya no existe. 
                   Por favor, intenta buscar la receta en nuestra sección de recetas.</p>
              )}
            </div>
          )}
          
          {isAdminRoute && isAdmin && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
              <p className="font-medium mb-2">¿Buscando una página de administración?</p>
              <p>Si estás intentando acceder a una funcionalidad de administrador, verifica la URL o utiliza los enlaces de navegación.</p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              className="bg-vegana-primary hover:bg-vegana-accent"
            >
              <Link to="/">Volver al inicio</Link>
            </Button>
            
            {isRecipeRoute && (
              <Button
                asChild
                variant="outline"
              >
                <Link to="/recetas">Ver todas las recetas</Link>
              </Button>
            )}
            
            {isAdmin && (
              <Button 
                asChild
                variant="outline"
              >
                <Link to="/admin/dashboard">Dashboard de Admin</Link>
              </Button>
            )}
            <Button 
              asChild
              variant="outline"
            >
              <Link to="/soporte">Contactar soporte</Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NotFound;
